import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

export default function Comms() {
  const qc = useQueryClient();
  const { data: templates } = useQuery({ queryKey: ['templates'], queryFn: () => api.get('/comms/templates').then((r) => r.data) });
  const { data: log } = useQuery({ queryKey: ['commlog'], queryFn: () => api.get('/comms/log').then((r) => r.data) });
  const { data: tenants } = useQuery({ queryKey: ['tenants'], queryFn: () => api.get('/tenants').then((r) => r.data) });
  const school = (tenants || []).find((t) => t.type === 'school');
  const [ann, setAnn] = useState({ title: '', body: '', audience: 'all', channel: 'in_app' });

  const sendMut = useMutation({
    mutationFn: () => api.post('/comms/announcements', { ...ann, tenant_id: school?.id }),
    onSuccess: (r) => { toast.success(`Announcement sent to ${r.data.recipients} users${r.data.email_status ? ' (email STUBBED)' : ''}`); setAnn({ ...ann, title: '', body: '' }); qc.invalidateQueries({ queryKey: ['commlog'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div data-testid="comms-page">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-6">Communication</h1>
      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <h2 className="font-display text-lg font-semibold mb-3">Send announcement</h2>
          <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
            <Input data-testid="ann-title-input" placeholder="Title" value={ann.title} onChange={(e) => setAnn({ ...ann, title: e.target.value })} />
            <textarea data-testid="ann-body-input" placeholder="Message... (guardians are only ever notified about their own children)" value={ann.body} onChange={(e) => setAnn({ ...ann, body: e.target.value })}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm h-24 focus:outline-none focus:ring-2 focus:ring-slate-900" />
            <div className="flex gap-2">
              <select data-testid="ann-audience-select" value={ann.audience} onChange={(e) => setAnn({ ...ann, audience: e.target.value })} className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-white">
                <option value="all">All</option><option value="staff">Staff</option><option value="guardians">Guardians</option><option value="students">Students</option>
              </select>
              <select data-testid="ann-channel-select" value={ann.channel} onChange={(e) => setAnn({ ...ann, channel: e.target.value })} className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-white">
                <option value="in_app">In-app</option><option value="email">Email (stub)</option>
              </select>
              <Button data-testid="ann-send-button" disabled={!ann.title || sendMut.isPending} onClick={() => sendMut.mutate()} className="bg-slate-900 hover:bg-slate-800 ml-auto">Send</Button>
            </div>
          </div>
          <h2 className="font-display text-lg font-semibold mt-6 mb-3">Delivery log</h2>
          <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="comm-log">
            {(log || []).map((l) => (
              <div key={l.id} className="p-3 text-sm">
                <p className="font-medium">{l.title} <span className="text-xs text-slate-400">- {l.audience} · {l.recipients} recipients</span></p>
                {l.email_status && <p className="font-mono-label text-[10px] uppercase text-amber-600">{l.email_status}</p>}
              </div>
            ))}
            {(log || []).length === 0 && <p className="p-4 text-sm text-slate-400">No announcements yet.</p>}
          </div>
        </div>
        <div>
          <h2 className="font-display text-lg font-semibold mb-3">Template registry <span className="font-mono-label text-[10px] uppercase text-slate-400">versioned · per-language · DLT-ready</span></h2>
          <div className="space-y-3" data-testid="templates-list">
            {(templates || []).map((t) => (
              <div key={t.id} className="bg-white border border-slate-200 rounded-md p-4 stagger-in">
                <div className="flex flex-wrap gap-2 items-center">
                  <p className="font-semibold text-sm">{t.name}</p>
                  <span className="font-mono-label text-[9px] uppercase bg-slate-100 px-2 py-0.5 rounded">v{t.version}</span>
                  <span className="font-mono-label text-[9px] uppercase bg-slate-100 px-2 py-0.5 rounded">{t.channel}</span>
                  <span className="font-mono-label text-[9px] uppercase bg-slate-100 px-2 py-0.5 rounded">{t.language}</span>
                  {t.dlt_template_id && <span className="font-mono-label text-[9px] uppercase bg-amber-100 text-amber-700 px-2 py-0.5 rounded">DLT: {t.dlt_template_id}</span>}
                </div>
                <p className="text-xs text-slate-500 mt-2">{t.subject}</p>
                <p className="text-xs text-slate-400">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
